import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  useGetMe,
  useUpdateMePreferences,
  getGetMeQueryKey,
  type ListInboundEmailsRead,
  type ListInboundEmailsView,
  type ListGmailMessagesRead,
  type ListGmailMessagesView,
} from "@workspace/api-client-react";

const READ_OPTIONS = ["all", "unread", "read"] as const;
const VIEW_OPTIONS = ["active", "snoozed", "pinned"] as const;

type ReadFilter = ListInboundEmailsRead & ListGmailMessagesRead;
type ViewFilter = ListInboundEmailsView & ListGmailMessagesView;

function isValidRead(v: unknown): v is ReadFilter {
  return typeof v === "string" && (READ_OPTIONS as readonly string[]).includes(v);
}
function isValidView(v: unknown): v is ViewFilter {
  return typeof v === "string" && (VIEW_OPTIONS as readonly string[]).includes(v);
}

export function useInboxReadFilter(): [ReadFilter, (v: ReadFilter) => void] {
  const queryClient = useQueryClient();
  const { data: me } = useGetMe();
  const updateMutation = useUpdateMePreferences();

  const serverValue: ReadFilter = isValidRead(me?.inboxReadFilter)
    ? me!.inboxReadFilter as ReadFilter
    : "all";

  const [optimistic, setOptimistic] = useState<ReadFilter | null>(null);
  const value = optimistic ?? serverValue;

  const setValue = (v: ReadFilter) => {
    setOptimistic(v);
    updateMutation.mutate(
      { data: { inboxReadFilter: v } },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: getGetMeQueryKey() });
          setOptimistic(null);
        },
        onError: () => {
          setOptimistic(null);
        },
      },
    );
  };

  return [value, setValue];
}

export function useInboxViewFilter(): [ViewFilter, (v: ViewFilter) => void] {
  const queryClient = useQueryClient();
  const { data: me } = useGetMe();
  const updateMutation = useUpdateMePreferences();

  const serverValue: ViewFilter = isValidView(me?.inboxViewFilter)
    ? me!.inboxViewFilter as ViewFilter
    : "active";

  const [optimistic, setOptimistic] = useState<ViewFilter | null>(null);
  const value = optimistic ?? serverValue;

  const setValue = (v: ViewFilter) => {
    setOptimistic(v);
    updateMutation.mutate(
      { data: { inboxViewFilter: v } },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: getGetMeQueryKey() });
          setOptimistic(null);
        },
        onError: () => {
          setOptimistic(null);
        },
      },
    );
  };

  return [value, setValue];
}
